<template>
  <el-dialog
    :title="isEdit ? 'Edit Booking' : 'New Booking'"
    :visible.sync="dialogVisible"
    width="800px"
    :before-close="handleClose"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="140px">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="Guest Name" prop="guest_name">
            <el-input v-model="form.guest_name" placeholder="Enter guest name"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Email" prop="guest_email">
            <el-input v-model="form.guest_email" placeholder="guest@email.com"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="Phone" prop="guest_phone">
            <el-input v-model="form.guest_phone" placeholder="+1234567890"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Number of Guests" prop="number_of_guests">
            <el-input-number
              v-model="form.number_of_guests"
              :min="1"
              :max="10"
              controls-position="right"
            ></el-input-number>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="Check-in Date" prop="check_in_date">
            <el-date-picker
              v-model="form.check_in_date"
              type="date"
              placeholder="Select date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :picker-options="checkInOptions"
              style="width: 100%"
            ></el-date-picker>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Check-out Date" prop="check_out_date">
            <el-date-picker
              v-model="form.check_out_date"
              type="date"
              placeholder="Select date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :picker-options="checkOutOptions"
              style="width: 100%"
            ></el-date-picker>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="Room Selection" prop="room_id">
        <el-select
          v-model="form.room_id"
          placeholder="Select room"
          filterable
          style="width: 100%"
          @change="onRoomChange"
        >
          <el-option
            v-for="room in availableRooms"
            :key="room.id"
            :label="`Room ${room.room_number} - ${room.room_type} ($${room.price_per_night}/night)`"
            :value="room.id"
          >
            <span style="float: left">Room {{ room.room_number }}</span>
            <span style="float: right; color: #8492a6; font-size: 13px">
              {{ room.room_type }} - ${{ room.price_per_night }}/night
            </span>
          </el-option>
        </el-select>
        <el-button
          type="text"
          @click="checkAvailability"
          :loading="checkingAvailability"
          style="margin-left: 10px"
        >
          Check Availability
        </el-button>
      </el-form-item>

      <el-form-item label="Special Requests">
        <el-input
          v-model="form.special_requests"
          type="textarea"
          :rows="3"
          placeholder="Any special requests or notes"
        ></el-input>
      </el-form-item>

      <el-form-item label="Payment Status">
        <el-radio-group v-model="form.payment_status">
          <el-radio label="pending">Pending</el-radio>
          <el-radio label="partial">Partial</el-radio>
          <el-radio label="paid">Paid</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="Amount Paid" v-if="form.payment_status !== 'pending'">
        <el-input-number
          v-model="form.paid_amount"
          :min="0"
          :max="totalAmount"
          :precision="2"
          controls-position="right"
        >
          <template slot="prepend">$</template>
        </el-input-number>
      </el-form-item>

      <!-- Booking Summary -->
      <el-divider></el-divider>
      <div class="booking-summary">
        <h4>Booking Summary</h4>
        <el-row>
          <el-col :span="12">
            <p><strong>Duration:</strong> {{ numberOfNights }} nights</p>
            <p><strong>Room Rate:</strong> ${{ roomRate }}/night</p>
          </el-col>
          <el-col :span="12">
            <p><strong>Subtotal:</strong> ${{ subtotal.toFixed(2) }}</p>
            <p><strong>Tax (10%):</strong> ${{ tax.toFixed(2) }}</p>
            <p><strong>Total Amount:</strong> <span class="total-amount">${{ totalAmount.toFixed(2) }}</span></p>
          </el-col>
        </el-row>
      </div>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="loading">
        {{ isEdit ? 'Update' : 'Create' }} Booking
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import bookingService from '@/services/booking.service'
import roomService from '@/services/room.service'

export default {
  name: 'CreateBookingDialog',
  props: {
    visible: Boolean,
    booking: Object
  },
  data () {
    return {
      dialogVisible: false,
      loading: false,
      checkingAvailability: false,
      form: {
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        number_of_guests: 1,
        check_in_date: '',
        check_out_date: '',
        room_id: null,
        special_requests: '',
        payment_status: 'pending',
        paid_amount: 0
      },
      availableRooms: [],
      selectedRoom: null,
      rules: {
        guest_name: [
          { required: true, message: 'Guest name is required', trigger: 'blur' }
        ],
        guest_email: [
          { required: true, message: 'Email is required', trigger: 'blur' },
          { type: 'email', message: 'Invalid email format', trigger: 'blur' }
        ],
        guest_phone: [
          { required: true, message: 'Phone number is required', trigger: 'blur' }
        ],
        number_of_guests: [
          { required: true, message: 'Number of guests is required', trigger: 'blur' }
        ],
        check_in_date: [
          { required: true, message: 'Check-in date is required', trigger: 'change' }
        ],
        check_out_date: [
          { required: true, message: 'Check-out date is required', trigger: 'change' }
        ],
        room_id: [
          { required: true, message: 'Please select a room', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    isEdit () {
      return !!this.booking
    },
    numberOfNights () {
      if (this.form.check_in_date && this.form.check_out_date) {
        const checkIn = new Date(this.form.check_in_date)
        const checkOut = new Date(this.form.check_out_date)
        const diff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24))
        return diff > 0 ? diff : 0
      }
      return 0
    },
    roomRate () {
      return this.selectedRoom ? this.selectedRoom.price_per_night : 0
    },
    subtotal () {
      return this.numberOfNights * this.roomRate
    },
    tax () {
      return this.subtotal * 0.1
    },
    totalAmount () {
      return this.subtotal + this.tax
    },
    checkInOptions () {
      return {
        disabledDate: (date) => {
          return date < new Date().setHours(0, 0, 0, 0)
        }
      }
    },
    checkOutOptions () {
      return {
        disabledDate: (date) => {
          if (this.form.check_in_date) {
            return date <= new Date(this.form.check_in_date)
          }
          return date < new Date().setHours(0, 0, 0, 0)
        }
      }
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
      if (val) {
        this.initializeForm()
      }
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    initializeForm () {
      if (this.isEdit) {
        this.form = { ...this.booking }
      } else {
        this.resetForm()
      }
      this.checkAvailability()
    },

    resetForm () {
      this.form = {
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        number_of_guests: 1,
        check_in_date: '',
        check_out_date: '',
        room_id: null,
        special_requests: '',
        payment_status: 'pending',
        paid_amount: 0
      }
      this.selectedRoom = null
    },

    async checkAvailability () {
      if (!this.form.check_in_date || !this.form.check_out_date) {
        this.$message.warning('Please select check-in and check-out dates')
        return
      }

      this.checkingAvailability = true
      try {
        const response = await bookingService.checkAvailability({
          check_in_date: this.form.check_in_date,
          check_out_date: this.form.check_out_date
        })
        this.availableRooms = response.data

        if (this.availableRooms.length === 0) {
          this.$message.warning('No rooms available for selected dates')
        }
      } catch (error) {
        this.$message.error('Failed to check availability')
      } finally {
        this.checkingAvailability = false
      }
    },

    onRoomChange (roomId) {
      this.selectedRoom = this.availableRooms.find(r => r.id === roomId)
    },

    handleClose () {
      this.$refs.form.resetFields()
      this.dialogVisible = false
    },

    async handleSubmit () {
      try {
        await this.$refs.form.validate()
        this.loading = true

        const data = {
          ...this.form,
          total_amount: this.totalAmount,
          status: 'confirmed'
        }

        if (this.isEdit) {
          await bookingService.updateBooking(this.booking.id, data)
          this.$message.success('Booking updated successfully')
        } else {
          await bookingService.createBooking(data)
          this.$message.success('Booking created successfully')
        }

        this.$emit('saved')
        this.handleClose()
      } catch (error) {
        if (error !== false) { // Validation error
          this.$message.error('Failed to save booking')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.booking-summary {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  margin-top: 10px;
}

.booking-summary h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #303133;
}

.booking-summary p {
  margin: 5px 0;
  color: #606266;
}

.total-amount {
  color: #409EFF;
  font-size: 18px;
  font-weight: bold;
}
</style>
